import React from 'react';
import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SpeedIcon from '@mui/icons-material/Speed';
import HandshakeIcon from '@mui/icons-material/Handshake';

const Services = () => {
  const theme = useTheme();

  const serviceItems = [
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 60 }} />,
      title: 'Quality Parts',
      description:
        'Quality parts on time are a guarantee. We specialize in small turned parts with a diameter of 1-5/8" and smaller.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 60 }} />,
      title: 'Quick Turnarounds',
      description:
        'Rapid quote responses and on-time delivery. We make reliability the standard, not the exception.',
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 60 }} />,
      title: 'Industry Experience',
      description:
        'Previous industries of experience include aerospace, automotive, medical, firearms, firefighting equipment, and RV.',
    },
  ];

  return (
    <Box
      id="services"
      sx={{
        pt: { xs: 10, md: 12 },
        pb: { xs: 8, md: 12 },
        backgroundColor: '#FAFAFA',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: {
            xs: 3,
            sm: 4,
            md: 6,
          },
        }}
      >
        <Typography
          variant="h2"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: { xs: 1, md: 2 },
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
          }}
        >
          What We Offer
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
            maxWidth: '700px',
            mx: 'auto',
            color: theme.palette.text.secondary,
            fontSize: { xs: '1rem', md: '1.15rem' },
            px: { xs: 2, md: 0 },
          }}
        >
          We provide precision machining services with a commitment to quality,
          speed, and customer collaboration.
        </Typography>
        {/* Sample parts visual + caption */}
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src="/parts.jpg"
              alt="Example of precision turned parts"
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: 'cover',
                borderRadius: 0,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              }}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography
              variant="h4"
              component="h3"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { xs: '1.4rem', md: '1.8rem' },
              }}
            >
              Real Parts. Real Precision.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                lineHeight: 1.8,
              }}
            >
              These are examples of the small turned parts JOCAST Machining
              produces every day&mdash;built to tight tolerances, consistent
              finishes, and delivered on schedule. From aerospace and
              automotive to medical, firearms, firefighting equipment, and RV
              applications, our work is designed to keep your production moving.
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {serviceItems.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  textAlign: 'center',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 0,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-5px)' },
                    boxShadow: { xs: 'none', md: `0 8px 24px rgba(0,0,0,0.15)` },
                    borderColor: theme.palette.tertiary.main,
                  },
                }}
              >
                <Box
                  sx={{
                    color: theme.palette.tertiary.main,
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ fontSize: { xs: 48, md: 60 } }}>
                    {service.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    mb: 2,
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {service.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.7,
                    fontSize: { xs: '0.95rem', md: '1rem' },
                  }}
                >
                  {service.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
